/**
 * Screen Wake Lock for the Now board's desk-display mode.
 *
 * Browsers release a screen wake lock whenever the page is hidden (tab switch,
 * screen off), so while enabled the lock is re-requested each time the page
 * becomes visible again.
 */

export function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

export interface ScreenWakeLock {
  enable(): void;
  disable(): void;
}

/** `onHeldChange` fires whenever the lock is acquired or released (by us or the browser). */
export function createScreenWakeLock(onHeldChange: (held: boolean) => void): ScreenWakeLock {
  let wanted = false;
  let requesting = false;
  // An acquire skipped because a request was in flight (e.g. the page went hidden and came
  // back meanwhile). Retried once that request settles, so a denial alone never loops.
  let retryAfterRequest = false;
  let sentinel: WakeLockSentinel | null = null;

  function setSentinel(next: WakeLockSentinel | null) {
    sentinel = next;
    onHeldChange(next !== null);
  }

  async function acquire() {
    if (!wanted || sentinel || !isWakeLockSupported()) return;
    if (document.visibilityState !== 'visible') return;
    if (requesting) {
      retryAfterRequest = true;
      return;
    }
    requesting = true;
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (!wanted) {
        void lock.release().catch(() => {});
        return;
      }
      lock.addEventListener('release', () => {
        if (sentinel === lock) setSentinel(null);
      });
      setSentinel(lock);
    } catch {
      // Denied (e.g. battery saver) or hidden mid-request; retried on the next visibilitychange.
    } finally {
      requesting = false;
      if (retryAfterRequest) {
        retryAfterRequest = false;
        void acquire();
      }
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') void acquire();
  }

  return {
    enable() {
      if (wanted) return;
      wanted = true;
      document.addEventListener('visibilitychange', onVisibilityChange);
      void acquire();
    },
    disable() {
      if (!wanted) return;
      wanted = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const lock = sentinel;
      if (!lock) return;
      setSentinel(null);
      void lock.release().catch(() => {});
    },
  };
}
